import React from 'react'
import { isArray, compact, noop } from 'lodash'

import { InputWrapper } from '../InputWrapper'
import Combobox from '../Combobox/Combobox'
import css from './style.scss'

/**
 * Initial values may be custom values that aren't in `options`; include them so they display.
 */
const withValues = (options, value) => {
  const values = compact(isArray(value) ? value : [value])
  const missing = values
    .filter((v) => !options.some((option) => option.value === v))
    .map((v) => ({ name: v, value: v }))
  return options.concat(missing)
}

/**
 * Controlled select component. Is compatible with redux-form Field.
 * Renders a native <select>, or an accessible combobox when `multi` or `allowCustom`.
 */
const Select = (props) => {
  const { input } = props
  // Default to the field name so <Label htmlFor='name'> associates without passing an id
  const id = props.id || input.name || props.name
  const errorId = id && !props.hideMeta ? `${id}-error` : undefined
  const meta = props.meta || {}
  const hasError = meta.error && !meta.active && (meta.dirty || meta.touched)
  const placeholder = typeof props.placeholder === 'string' ? props.placeholder : 'Select...'
  const options = withValues(props.options, input.value)
  const onBlur = input.onBlur ? () => input.onBlur(input.value) : noop

  const a11yProps = {
    // <Label htmlFor={id}> renders this id; it also lets a heading name the field
    'aria-labelledby': id && !props['aria-label'] ? `${id}-label` : undefined,
    'aria-label': props['aria-label'],
    'aria-describedby': errorId,
    'aria-invalid': hasError || undefined
  }

  let control
  if (props.multi || props.allowCustom) {
    const toOption = (value) => {
      const option = options.find((o) => o.value === value)
      return { label: option.name, value }
    }
    const value = props.multi
      ? compact(input.value || []).map(toOption)
      : (input.value ? toOption(input.value) : null)

    control = (
      <Combobox
        id={id}
        {...a11yProps}
        options={options.map((o) => ({ label: o.name, value: o.value }))}
        value={value}
        onChange={(selected) => {
          if (props.multi) input.onChange(selected.map((o) => o.value))
          else input.onChange(selected ? selected.value : null)
        }}
        onBlur={onBlur}
        multi={!!props.multi}
        allowCustom={!!props.allowCustom}
        placeholder={placeholder}
        disabled={props.disabled}
      />
    )
  } else {
    const selectedIndex = options.findIndex((o) => o.value === input.value)

    control = (
      <select
        id={id}
        {...a11yProps}
        className={css.select}
        value={selectedIndex === -1 ? '' : String(selectedIndex)}
        onChange={(e) => input.onChange(e.target.value === '' ? null : options[Number(e.target.value)].value)}
        onBlur={onBlur}
        disabled={props.disabled}
      >
        <option value=''>{placeholder}</option>
        {options.map((option, index) => (
          <option key={index} value={String(index)}>{option.name}</option>
        ))}
      </select>
    )
  }

  const classes = [
    css.wrapper,
    props.grey ? css.grey : null
  ]

  return (
    <div className={compact(classes).join(' ')}>
      <InputWrapper {...meta} hideMeta={props.hideMeta} errorId={errorId}>
        {control}
        {props.afterControl}
      </InputWrapper>
    </div>
  )
}

Select.propTypes = {
  options: React.PropTypes.arrayOf(React.PropTypes.shape({
    name: React.PropTypes.string,
    value: React.PropTypes.any
  })).isRequired,
  allowCustom: React.PropTypes.bool,
  multi: React.PropTypes.bool,
  placeholder: React.PropTypes.node,
  id: React.PropTypes.string,
  name: React.PropTypes.string,
  'aria-label': React.PropTypes.string,
  input: React.PropTypes.object,
  meta: React.PropTypes.object,
  hideMeta: React.PropTypes.bool,
  disabled: React.PropTypes.bool,
  /* Allows for alternative styling for Select, used in filter dropdowns */
  grey: React.PropTypes.bool,
  /* Extra content rendered inside InputWrapper, above the reserved error text area */
  afterControl: React.PropTypes.node
}

Select.defaultProps = {
  hideMeta: false,
  disabled: false
}

export default Select
